import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TrendingUp, Users, ShieldCheck, Activity, CheckCircle2, Smartphone, CreditCard, Bot, KeyRound, Database } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";

const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function KpiCard({ label, value, sub, accent }) {
  return (
    <div className={`rounded-3xl p-6 shadow-ambient ${accent ? "glass-blush" : "bg-surface-container-lowest"}`} data-testid="kpi-card">
      <p className="text-xs uppercase tracking-widest text-outline">{label}</p>
      <p className="font-headline text-4xl text-primary mt-2 font-light">{value}</p>
      {sub && <p className="text-sm text-on-surface-variant mt-1">{sub}</p>}
    </div>
  );
}

function Overview() {
  const [k, setK] = useState(null);
  useEffect(() => { api.get("/admin/kpis").then((r) => setK(r.data)).catch(() => {}); }, []);
  if (!k) return <div className="text-on-surface-variant">Loading…</div>;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <KpiCard label="Booking conversion" value={`${k.bookingConversion}%`} sub="Target > 22%" accent />
        <KpiCard label="Bot first-contact resolution" value={`${k.fcrRate}%`} sub="Target > 65%" />
        <KpiCard label="No-show rate" value={`${k.noShowRate}%`} sub="Target < 4.5%" />
        <KpiCard label="CSAT" value={`${k.csat}/5`} sub="Target > 4.8" />
        <KpiCard label="Confirmed bookings" value={k.confirmedBookings} />
        <KpiCard label="Active escalations" value={k.activeEscalations} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard label="Total users" value={k.totalUsers} />
        <KpiCard label="Clients" value={k.totalClients} />
        <KpiCard label="Bot messages" value={k.botMessages} />
        <KpiCard label="Total messages" value={k.totalMessages} />
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const roleStyle = { admin: "bg-secondary-container text-secondary-on-container", coach: "bg-primary-fixed text-primary-on-container", client: "bg-surface-container-high text-on-surface-variant" };
  return (
    <div className="bg-surface-container-lowest rounded-3xl p-3 sm:p-4 shadow-ambient overflow-hidden">
      {users.map((u, i) => (
        <div key={u.user_id} className={`flex items-center justify-between gap-3 px-3 sm:px-4 py-3.5 rounded-2xl ${i % 2 === 0 ? "bg-surface-container-low" : ""}`} data-testid="admin-user-row">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-full bg-surface-container-highest flex items-center justify-center font-headline text-primary text-sm shrink-0">{u.firstName?.[0]}{u.lastName?.[0]}</span>
            <div className="min-w-0">
              <p className="font-medium text-on-surface truncate">{u.firstName} {u.lastName}</p>
              <p className="text-xs text-on-surface-variant truncate">{u.email}</p>
            </div>
          </div>
          <span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full shrink-0 ${roleStyle[u.role]}`}>{u.role}</span>
        </div>
      ))}
    </div>
  );
}

const INT_ICONS = { "WhatsApp Business API": Smartphone, "Stripe Payments": CreditCard, "AI Chatbot (Claude)": Bot, "Google SSO": KeyRound, "MongoDB": Database };

function Integrations() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/integrations").then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-3">
      {list.map((it) => {
        const Icon = INT_ICONS[it.name] || Activity;
        return (
          <div key={it.name} className="bg-surface-container-lowest rounded-3xl p-5 shadow-ambient flex items-center justify-between" data-testid="integration-row">
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-2xl bg-primary-fixed flex items-center justify-center"><Icon className="w-5 h-5 text-primary" strokeWidth={1.5} /></span>
              <div>
                <p className="font-medium text-on-surface">{it.name}</p>
                <p className="text-xs text-on-surface-variant">{it.detail}</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm text-primary">
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> <span className="capitalize">{it.status}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-3">
      {logs.length === 0 ? (
        <div className="bg-surface-container-low rounded-3xl p-8 text-center text-on-surface-variant">No activity logged yet.</div>
      ) : logs.map((l) => (
        <div key={l.notif_id} className="bg-surface-container-lowest rounded-2xl p-4 shadow-ambient flex items-center justify-between" data-testid="audit-row">
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-surface-container-high text-on-surface-variant">{l.type}</span>
            <div>
              <p className="text-sm font-medium text-on-surface">{l.title}</p>
              <p className="text-xs text-on-surface-variant">{l.body}</p>
            </div>
          </div>
          <span className="text-xs text-outline shrink-0">{fmtDate(l.createdAt)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [params] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const titles = { overview: "Platform metrics", users: "User management", integrations: "Integration health", audit: "Audit log" };
  const icons = { overview: TrendingUp, users: Users, integrations: ShieldCheck, audit: Activity };
  const Icon = icons[tab];
  return (
    <AppShell>
      <div className="mb-8 flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl zenith-cta flex items-center justify-center"><Icon className="w-5 h-5 text-white" strokeWidth={1.5} /></span>
        <h1 className="text-4xl sm:text-5xl font-headline font-light text-primary tracking-tight">{titles[tab]}</h1>
      </div>
      {tab === "overview" && <Overview />}
      {tab === "users" && <UsersTab />}
      {tab === "integrations" && <Integrations />}
      {tab === "audit" && <Audit />}
    </AppShell>
  );
}
